import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.ui.unit.IntOffset
import kotlin.math.roundToInt
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.exclude
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.onConsumedWindowInsetsChanged
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.layout.requiredWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Surface
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.BottomSheetScaffold
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SheetValue
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberBottomSheetScaffoldState
import androidx.compose.material3.rememberStandardBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import models.DomainGame
import models.GameSettings
import models.LimitedTimeGameModes
import models.Player
import models.RoundModifier
import util.networkAvailableFlow
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*

@Composable
fun BankoGameScreen(
    bankoGameScreenViewModel: BankoGameScreenViewModel,
    navigateBack: () -> Unit = {},
    onShowSnackbar: (String) -> Unit = {},
) {
    val uiState = bankoGameScreenViewModel.bankoGameScreenUiState

    val gameEvent = uiState.gameEventFlow.collectAsState().value

    val playerWasKickedString = stringResource(Res.string.snackbar_player_kicked)
    val gameUnavailableString = stringResource(Res.string.snackbar_game_unavailable)
    val gameFinishedString = stringResource(Res.string.snackbar_game_finished)
    LaunchedEffect(gameEvent) {
        when (gameEvent) {
            is GameScreenEvent.PlayerWasKicked -> {
                navigateBack()
                onShowSnackbar(playerWasKickedString)
                uiState.gameEventFlow.reset()
            }
            is GameScreenEvent.GameUnavailable -> {
                navigateBack()
                onShowSnackbar(gameUnavailableString)
                uiState.gameEventFlow.reset()
            }
            is GameScreenEvent.GameFinished -> {
                navigateBack()
                onShowSnackbar(gameFinishedString)
                uiState.gameEventFlow.reset()
            }
            is GameScreenEvent.GameFinishedWithMessage -> {
                navigateBack()
                onShowSnackbar(gameEvent.message)
                uiState.gameEventFlow.reset()
            }
            is GameScreenEvent.ShowSnackbar -> {
                onShowSnackbar(gameEvent.message)
                uiState.gameEventFlow.reset()
            }
            is GameScreenEvent.PracticeCompleted -> {
                // A short beat so the final roll visibly lands, then straight back to the start
                // screen — no interstitial, no game-over ceremony. The start screen opens the dice
                // picker (via PracticeGameSession's showcase flag) on the new Training Wheels.
                delay(1200)
                navigateBack()
                if (gameEvent.unlockedTrainingDice) {
                    onShowSnackbar(PRACTICE_DICE_UNLOCKED_MESSAGE)
                }
                uiState.gameEventFlow.reset()
            }
            null -> { /* No event to handle */ }
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        // Main game content
        BankoGameScreenContent(
            uiState = uiState,
            navigateBack = navigateBack
        )

        GameSeasonalEffects(uiState)

        // Round overlay
        RoundOverlayComponent(
            uiState = uiState
        )
    }
}

/**
 * The event decoration over the whole game screen, running up behind the status bar as on iOS.
 * Non-interactive; hidden when the player turns Seasonal effects off.
 */
@Composable
private fun GameSeasonalEffects(uiState: BankoGameScreenUiState) {
    val game by uiState.gameFlow.collectAsState()
    val limitedTimeModifier = game?.settings?.let { LimitedTimeGameModes.roundModifierFor(it) }
    val seasonalEffects by SeasonalEffectsPreferences.enabled.collectAsState()
    if (!seasonalEffects || limitedTimeModifier == null) return
    val layerModifier = Modifier.fillMaxSize().extendIntoStatusBar()

    if (limitedTimeModifier == RoundModifier.FALL_HARVEST || limitedTimeModifier == RoundModifier.HALLOWEEN) {
        SeasonalEventBackground(limitedTimeModifier, modifier = layerModifier)
    }

    if (limitedTimeModifier == RoundModifier.FIREWORKS) {
        FireworksStreamerBackground(modifier = layerModifier.alpha(0.66f))
        FireworksGameEventEffects(
            game = game,
            enabled = true,
            modifier = layerModifier,
        )
    }
}

@Composable
private fun RoundOverlayComponent(
    uiState: BankoGameScreenUiState
) {
    val roundOverlayState by uiState.roundOverlayStateFlow.collectAsState()

    if (roundOverlayState.showOverlay) {
        val alpha = remember { Animatable(1f) }
        LaunchedEffect(roundOverlayState.roundNumber) {
            alpha.snapTo(1f)
            // Hold longer when there's a Ginko modifier — its title and tagline need to be read.
            val holdMillis = if (roundOverlayState.modifier == RoundModifier.NONE) 1400L else 2600L
            delay(holdMillis)
            alpha.animateTo(0f, animationSpec = tween(durationMillis = 1000))
            uiState.onRoundOverlayShown()
        }
        // The app shell dims the status bar and ad banner in step, so the scrim reads edge to edge.
        SideEffect { EdgeScrim.alpha.floatValue = 0.5f * alpha.value }
        DisposableEffect(Unit) { onDispose { EdgeScrim.alpha.floatValue = 0f } }
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.5f * alpha.value))
                .alpha(alpha.value),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    text = stringResource(Res.string.round_overlay, roundOverlayState.roundNumber),
                    style = MaterialTheme.typography.displayLarge,
                    color = Color.White
                )
                val modifierTitle = roundModifierTitle(roundOverlayState.modifier)
                if (modifierTitle != null) {
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = modifierTitle,
                        style = MaterialTheme.typography.headlineMedium,
                        textAlign = TextAlign.Center,
                        color = MaterialTheme.colorScheme.primary,
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = roundModifierDescription(roundOverlayState.modifier),
                        style = MaterialTheme.typography.titleMedium,
                        textAlign = TextAlign.Center,
                        color = Color.White,
                    )
                }
            }
        }
    }
}

/** The headline shown in the round overlay for a Ginko modifier, or null for standard rounds. */
@Composable
internal fun roundModifierTitle(modifier: RoundModifier): String? = when (modifier) {
    RoundModifier.NONE -> null
    RoundModifier.DOUBLE_TROUBLE -> stringResource(Res.string.ginko_double_trouble_title)
    RoundModifier.FLIPSIDE -> stringResource(Res.string.ginko_flipside_title)
    RoundModifier.JACKPOT -> stringResource(Res.string.ginko_jackpot_title)
    RoundModifier.HIGH_STAKES -> stringResource(Res.string.ginko_high_stakes_title)
    RoundModifier.LOCKDOWN -> stringResource(Res.string.ginko_lockdown_title)
    RoundModifier.FIREWORKS -> stringResource(Res.string.fireworks_mode_title)
    RoundModifier.FALL_HARVEST -> stringResource(Res.string.fall_harvest_title)
    RoundModifier.HALLOWEEN -> stringResource(Res.string.halloween_title)
}

@Composable
private fun roundModifierDescription(modifier: RoundModifier): String = when (modifier) {
    RoundModifier.NONE -> ""
    RoundModifier.DOUBLE_TROUBLE -> stringResource(Res.string.ginko_double_trouble_desc)
    RoundModifier.FLIPSIDE -> stringResource(Res.string.ginko_flipside_desc)
    RoundModifier.JACKPOT -> stringResource(Res.string.ginko_jackpot_desc)
    RoundModifier.HIGH_STAKES -> stringResource(Res.string.ginko_high_stakes_desc)
    RoundModifier.LOCKDOWN -> stringResource(Res.string.ginko_lockdown_desc)
    RoundModifier.FIREWORKS -> stringResource(Res.string.fireworks_mode_desc)
    RoundModifier.FALL_HARVEST -> stringResource(Res.string.fall_harvest_desc)
    RoundModifier.HALLOWEEN -> stringResource(Res.string.halloween_desc)
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun BankoGameScreenContent(
    uiState: BankoGameScreenUiState,
    navigateBack: () -> Unit = {},
) {
    // Collect state from StateFlows
    val game by uiState.gameFlow.collectAsState()

    val isNetworkGame = uiState.gameCode != "LOCAL"

    // Network connectivity banner logic - only check for network games
    val networkAvailable by if (isNetworkGame) {
        networkAvailableFlow().collectAsState(initial = true)
    } else {
        // For local games, always return true (no network issues)
        remember { mutableStateOf(true) }
    }

    // Debounce network state changes to prevent flickering
    var stableNetworkAvailable by remember { mutableStateOf(true) }
    LaunchedEffect(networkAvailable) {
        if (!networkAvailable) {
            // Wait a bit before showing disconnection to avoid false positives
            delay(2000)
            if (!networkAvailable) {
                stableNetworkAvailable = false
            }
        } else {
            stableNetworkAvailable = true
        }
    }

    val showPlayerPickerDialog = remember { mutableStateOf(false) }
    // True while the dice-roll animation plays. Hoisted here because the roll button (in the
    // pinned bottom sheet) and the dice animation (in the board content) live in different trees.
    val isRolling = remember { mutableStateOf(false) }

    // Derived state
    val gameState = remember(game, uiState.playerName) {
        GameScreenState.from(game, uiState.playerName)
    }

    // Leaving mid-game reads as lost progress; confirm before tearing the screen down. Applies to
    // both the system back gesture and the top-bar back arrow. Confirming actually leaves: the
    // player is removed from the game (a leaving host first hands hosting to the next player on
    // their own device, or the game is deleted when no such player remains). Leaving the lobby
    // does the same without asking — nothing to lose yet — while backing out after game over
    // neither asks nor removes, so the roster stays intact for a rematch.
    var showLeaveGameDialog by remember { mutableStateOf(false) }
    var leaveInProgress by remember { mutableStateOf(false) }
    val leaveScope = rememberCoroutineScope()
    val leaveNeedsConfirmation = gameState.isStarted && !gameState.isGameOver
    // Mirrors the successor pick in onLeaveGame, purely to word the dialog.
    val successorName = remember(game, uiState.playerName) {
        leaveSuccessorName(game, uiState.playerName)
    }
    val leaveAndNavigate: () -> Unit = {
        if (!leaveInProgress) {
            leaveInProgress = true
            leaveScope.launch {
                val didLeave = uiState.onLeaveGame()
                leaveInProgress = false
                if (didLeave) {
                    navigateBack()
                }
            }
        }
    }
    val confirmableNavigateBack: () -> Unit = {
        when {
            leaveInProgress -> Unit
            leaveNeedsConfirmation -> showLeaveGameDialog = true
            !gameState.isGameOver -> leaveAndNavigate()
            else -> navigateBack()
        }
    }
    PlatformBackHandler(enabled = !gameState.isGameOver) {
        confirmableNavigateBack()
    }
    if (showLeaveGameDialog) {
        AlertDialog(
            onDismissRequest = {
                if (!leaveInProgress) {
                    showLeaveGameDialog = false
                }
            },
            title = { Text(stringResource(Res.string.leave_game_title)) },
            text = {
                Text(
                    when {
                        !isNetworkGame ->
                            stringResource(Res.string.leave_game_message_local)
                        gameState.isHost && successorName != null ->
                            stringResource(Res.string.leave_game_message_network_host, successorName)
                        gameState.isHost ->
                            stringResource(Res.string.leave_game_message_network_host_last)
                        else ->
                            stringResource(Res.string.leave_game_message_network)
                    }
                )
            },
            confirmButton = {
                TextButton(
                    enabled = !leaveInProgress,
                    onClick = {
                        showLeaveGameDialog = false
                        leaveAndNavigate()
                    }
                ) {
                    Text(stringResource(Res.string.leave_game_confirm))
                }
            },
            dismissButton = {
                TextButton(
                    enabled = !leaveInProgress,
                    onClick = { showLeaveGameDialog = false },
                ) {
                    Text(stringResource(Res.string.leave_game_cancel))
                }
            },
        )
    }
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    val lobbyAdsDisabled by LobbyAdState.adsDisabledForLobby.collectAsState()
    var endGameAdShownForGame by remember(game?.joinCode, uiState.gameCode) {
        mutableStateOf(false)
    }
    var reviewRequestedForGame by remember(game?.joinCode, uiState.gameCode) {
        mutableStateOf(false)
    }

    LaunchedEffect(
        gameState.isStarted,
        gameState.isGameOver,
        purchaseState.adsRemoved,
        purchaseState.entitlementsResolved,
        purchaseState.isPurchasesSupported,
        lobbyAdsDisabled,
        game?.hostPerksActive,
    ) {
        // The practice round never ends in an interstitial or a review prompt — it's a teaching
        // game, and its completion navigates back to the start screen instead of game-over.
        if (uiState.isPracticeGame) return@LaunchedEffect

        val purchasesReady = purchaseState.entitlementsResolved || !purchaseState.isPurchasesSupported
        val adsDisabled = purchaseState.adsRemoved || lobbyAdsDisabled || game?.hostPerksActive == true

        if (!gameState.isGameOver) {
            endGameAdShownForGame = false
            reviewRequestedForGame = false
            if (gameState.isStarted && purchasesReady && !adsDisabled) {
                EndGameAds.preload()
            }
            return@LaunchedEffect
        }

        if (
            gameState.isStarted &&
            purchasesReady &&
            !adsDisabled &&
            !endGameAdShownForGame
        ) {
            endGameAdShownForGame = true
            EndGameAds.show(
                onDismissed = {
                    // The player just finished a game and closed the ad — the calm high point.
                    if (!reviewRequestedForGame) {
                        reviewRequestedForGame = true
                        AppReview.requestReview()
                    }
                }
            )
        } else if (
            gameState.isStarted &&
            purchasesReady &&
            adsDisabled &&
            !reviewRequestedForGame
        ) {
            reviewRequestedForGame = true
            AppReview.requestReview()
        }
    }

    // Live game analytics, one tap from the top bar once dice are rolling. The sheet reads the same
    // collected game state, so it keeps updating while it's open.
    var showGameStats by remember { mutableStateOf(false) }
    val openGameStats: (() -> Unit)? = if (gameState.isStarted && game != null) {
        {
            showGameStats = true
            Analytics.statsViewed(
                if (gameState.isGameOver) Analytics.StatsSource.GameOver else Analytics.StatsSource.InGame
            )
        }
    } else {
        null
    }
    val statsGame = game
    if (showGameStats && statsGame != null) {
        GameStatsSheet(
            game = statsGame,
            selfName = uiState.playerName,
            onDismiss = { showGameStats = false },
        )
    }

    val shouldShowManualDiceGrid = remember(game, gameState) {
        manualDiceGridVisible(game, gameState)
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        // A foldable held like a book gets the same side-by-side layout, one page each.
        val isBookLayout = LocalFoldPosture.current is FoldPosture.Book
        val isPhoneLandscape = isConstrainedPhoneLandscape(maxWidth, maxHeight) || isBookLayout

        if (isPhoneLandscape) {
            if (showPlayerPickerDialog.value) {
                PlayerPickerDialog(
                    showPlayerPickerDialog = showPlayerPickerDialog,
                    uiState = uiState,
                )
            }

            Scaffold(
                topBar = {
                    GameScreenTopBar(
                        gameCode = uiState.gameCode,
                        playerName = uiState.playerName,
                        hostPlayerName = gameState.hostPlayer?.name,
                        isHost = gameState.isHost,
                        navigateBack = confirmableNavigateBack,
                        isNetworkGame = isNetworkGame,
                        isPracticeGame = uiState.isPracticeGame,
                        networkAvailable = stableNetworkAvailable,
                        onOpenStats = openGameStats,
                        limitedTimeModifier = game?.settings?.let { LimitedTimeGameModes.roundModifierFor(it) },
                    )
                },
                containerColor = MaterialTheme.colorScheme.background,
            ) { innerPadding ->
                GameScreenMainContent(
                    uiState = uiState,
                    game = game,
                    gameState = gameState,
                    innerPadding = innerPadding,
                    isPhoneLandscapeLayout = true,
                    isBookLayout = isBookLayout,
                    shouldShowManualDiceGrid = shouldShowManualDiceGrid,
                    showPlayerPickerDialog = showPlayerPickerDialog,
                    isRolling = isRolling,
                    onFinishGame = {
                        if (uiState.gameCode == "LOCAL") {
                            navigateBack()
                        } else {
                            uiState.onGameFinished()
                        }
                    },
                )
            }
        } else {
            val showStartGameHint =
                !gameState.isStarted && gameState.isHost && (game?.players?.size ?: 0) < 2
            val bottomSheetState = rememberStandardBottomSheetState(
                initialValue = SheetValue.Expanded
            )
            val bottomSheetScaffoldState = rememberBottomSheetScaffoldState(
                bottomSheetState = bottomSheetState
            )
            // The sheet runs edge to edge behind the navigation bar; only its buttons sit above it.
            // Whatever an ancestor already consumed (the ad banner, when it's showing) isn't added again.
            var consumedInsets by remember { mutableStateOf(WindowInsets(0)) }
            val density = LocalDensity.current
            val navigationBarHeight = with(density) {
                WindowInsets.navigationBars.exclude(consumedInsets).getBottom(density).toDp()
            }

            BottomSheetScaffold(
                modifier = Modifier.onConsumedWindowInsetsChanged { consumedInsets = it },
                topBar = {
                    GameScreenTopBar(
                        gameCode = uiState.gameCode,
                        playerName = uiState.playerName,
                        hostPlayerName = gameState.hostPlayer?.name,
                        isHost = gameState.isHost,
                        navigateBack = confirmableNavigateBack,
                        isNetworkGame = isNetworkGame,
                        isPracticeGame = uiState.isPracticeGame,
                        networkAvailable = stableNetworkAvailable,
                        onOpenStats = openGameStats,
                        limitedTimeModifier = game?.settings?.let { LimitedTimeGameModes.roundModifierFor(it) },
                    )
                },
                sheetDragHandle = {
                    if (shouldShowManualDiceGrid) {
                        BottomSheetDefaults.DragHandle()
                    } else {
                        Spacer(modifier = Modifier.padding(8.dp))
                    }
                },
                sheetSwipeEnabled = shouldShowManualDiceGrid,
                containerColor = MaterialTheme.colorScheme.background,
                scaffoldState = bottomSheetScaffoldState,
                // The sheet isn't swipeable pre-game, so the peek height must fit the whole
                // content — including the "add another player" hint under the Start button.
                sheetPeekHeight = navigationBarHeight + when {
                    shouldShowManualDiceGrid -> 108.dp
                    showStartGameHint -> 104.dp
                    else -> 76.dp
                },
                sheetContent = {
                    GameScreenBottomSheet(
                        modifier = Modifier.padding(bottom = navigationBarHeight),
                        uiState = uiState,
                        game = game,
                        gameState = gameState,
                        shouldShowManualDiceGrid = shouldShowManualDiceGrid,
                        showPlayerPickerDialog = showPlayerPickerDialog,
                        isRolling = isRolling,
                        navigateBack = navigateBack
                    )
                }
            ) { innerPadding ->
                GameScreenMainContent(
                    uiState = uiState,
                    game = game,
                    gameState = gameState,
                    innerPadding = innerPadding,
                    isRolling = isRolling,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun GameScreenTopBar(
    gameCode: String,
    playerName: String,
    hostPlayerName: String?,
    isHost: Boolean,
    navigateBack: () -> Unit,
    isNetworkGame: Boolean,
    networkAvailable: Boolean,
    isPracticeGame: Boolean = false,
    onOpenStats: (() -> Unit)? = null,
    limitedTimeModifier: RoundModifier? = null,
) {
    Column {
        if (isNetworkGame && !networkAvailable) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFB71C1C)) // Red
                    .height(28.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = "Network connection lost. Changes may not sync.",
                    color = Color.White,
                    style = MaterialTheme.typography.bodySmall,
                    textAlign = TextAlign.Center
                )
            }
        }
        CenterAlignedTopAppBar(
            modifier = Modifier.padding(horizontal = 8.dp),
            // No bar fill: the floating chrome buttons sit straight on the board's background, like
            // the iOS top row, instead of on a solid strip that merely matches it.
            colors = TopAppBarDefaults.topAppBarColors(
                containerColor = Color.Transparent,
                scrolledContainerColor = Color.Transparent,
            ),
            navigationIcon = {
                BankoChromeIconButton(
                    icon = BankoIcons.ArrowBack,
                    contentDescription = "Back",
                    onClick = navigateBack,
                    tint = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.padding(start = 4.dp),
                )
            },
            actions = {
                if (onOpenStats != null) {
                    BankoChromeIconButton(
                        icon = BankoIcons.BarChart,
                        contentDescription = stringResource(Res.string.open_game_stats),
                        onClick = onOpenStats,
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                }
                // Rules are one tap away mid-game (first-timers shouldn't have to leave the game
                // to look up what banking does). Sharing moved to the lobby's Invite Friends tile.
                var showRules by remember { mutableStateOf(false) }
                BankoChromePillButton(
                    text = stringResource(Res.string.rules),
                    onClick = { showRules = true },
                    modifier = Modifier.padding(end = 4.dp),
                )
                if (showRules) {
                    RulesBottomSheet(
                        bottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
                        onDismissRequest = { showRules = false },
                        eventRules = limitedTimeModifier?.let { event ->
                            (roundModifierTitle(event) ?: "") to roundModifierDescription(event)
                        },
                    )
                }
            },
            title = {
                // Greeting leads (the human moment); the game code sits beneath as a small,
                // de-emphasized label so the chrome doesn't out-shout the board.
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // The table's name: whose table it is online, or what kind of game it is on one
                    // device (where there's no host to name and no code to share).
                    val greetingText = when {
                        isPracticeGame -> stringResource(Res.string.table_title_practice)
                        !isNetworkGame -> stringResource(Res.string.table_title_pass_and_play)
                        isHost -> stringResource(Res.string.host_greeting)
                        else -> stringResource(Res.string.player_greeting, hostPlayerName ?: "")
                    }
                    Text(
                        // Wrap content (not fillMaxWidth): CenterAlignedTopAppBar centers the title
                        // placeable on true screen center only when it's content-sized. Filling the
                        // slot makes it span the asymmetric space left of the actions, so without the
                        // share icon (non-host / single-device) the text drifts right of center.
                        text = greetingText,
                        textAlign = TextAlign.Center,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onBackground,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (isNetworkGame) {
                        Text(
                            text = stringResource(Res.string.game_code_format, gameCode),
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.labelMedium,
                            letterSpacing = 1.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
            },
        )
    }
}

@Composable
private fun GameScreenMainContent(
    uiState: BankoGameScreenUiState,
    game: DomainGame?,
    gameState: GameScreenState,
    innerPadding: PaddingValues,
    isRolling: androidx.compose.runtime.MutableState<Boolean>,
    isPhoneLandscapeLayout: Boolean = false,
    isBookLayout: Boolean = false,
    shouldShowManualDiceGrid: Boolean = false,
    showPlayerPickerDialog: androidx.compose.runtime.MutableState<Boolean>? = null,
    onFinishGame: (() -> Unit)? = null,
) {
    // When a nudge aimed at me arrives, wobble the whole board — a visible cue that works on every
    // platform (the haptic does nothing on web/desktop).
    val nudgeCount by uiState.nudgeReceivedFlow.collectAsState()
    val shakeOffset = remember { Animatable(0f) }
    LaunchedEffect(nudgeCount) {
        if (nudgeCount > 0) {
            repeat(3) {
                shakeOffset.animateTo(20f, tween(45))
                shakeOffset.animateTo(-20f, tween(45))
            }
            shakeOffset.animateTo(0f, tween(45))
        }
    }

    val boardModifier = Modifier
            .offset { IntOffset(shakeOffset.value.roundToInt(), 0) }
            .padding(innerPadding)
            .consumeWindowInsets(innerPadding)
            .safeDrawingPadding()
            .fillMaxSize()

    val tabletop = LocalFoldPosture.current as? FoldPosture.Tabletop
    Box(modifier = boardModifier) {
        if (isPhoneLandscapeLayout) {
            PhoneLandscapeGameLayout(
                uiState = uiState,
                game = game,
                gameState = gameState,
                shouldShowManualDiceGrid = shouldShowManualDiceGrid,
                showPlayerPickerDialog = showPlayerPickerDialog,
                isRolling = isRolling,
                onFinishGame = onFinishGame,
                isBookLayout = isBookLayout,
                modifier = Modifier.fillMaxSize(),
            )
        } else if (tabletop != null && gameState.isStarted && !gameState.isGameOver) {
            TabletopGameLayout(
                uiState = uiState,
                game = game,
                gameState = gameState,
                isRolling = isRolling,
                hinge = tabletop,
            )
        } else {
            BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
                // The grid never gets to squeeze the play area (or the lobby settings) off-screen:
                // past its share of the height it scrolls internally. Mid-game the dice/actions
                // matter more than seeing every scorecard at once, so the cap is tighter.
                val gridMaxHeight = maxHeight * if (gameState.isStarted) 0.34f else 0.48f
                Column(
                    modifier = Modifier.fillMaxSize(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    if (!gameState.isGameOver) {
                        Box(
                            modifier = Modifier.fillMaxWidth(),
                            contentAlignment = Alignment.Center,
                        ) {
                            Box(
                                modifier = Modifier
                                    .widthIn(max = 10_000.dp)
                                    .fillMaxWidth()
                                    .heightIn(max = gridMaxHeight)
                                    .verticalScroll(rememberScrollState()),
                            ) {
                                PlayersGrid(
                                    game = game,
                                    playerName = uiState.playerName,
                                    gameState = gameState,
                                    uiState = uiState
                                )
                            }
                        }
                    }

                    if (gameState.isStarted) {
                        CommentaryTicker(
                            uiState = uiState,
                            modifier = Modifier.widthIn(max = 10_000.dp),
                        )
                    }

                    GameContentSection(
                        game = game,
                        gameState = gameState,
                        uiState = uiState,
                        isRolling = isRolling,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
        }

    }
}

@Composable
private fun PhoneLandscapeGameLayout(
    uiState: BankoGameScreenUiState,
    game: DomainGame?,
    gameState: GameScreenState,
    shouldShowManualDiceGrid: Boolean,
    showPlayerPickerDialog: androidx.compose.runtime.MutableState<Boolean>?,
    isRolling: androidx.compose.runtime.MutableState<Boolean>,
    onFinishGame: (() -> Unit)?,
    isBookLayout: Boolean = false,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier.padding(horizontal = 10.dp, vertical = 6.dp),
        contentAlignment = Alignment.TopCenter,
    ) {
        Row(
            modifier = Modifier
                // A book spans the whole spread so its halves meet at the fold.
                .widthIn(max = if (isBookLayout) 10_000.dp else 960.dp)
                .fillMaxWidth()
                .fillMaxHeight(),
            // Keeps both pages' content off the crease.
            horizontalArrangement = Arrangement.spacedBy(if (isBookLayout) 32.dp else 10.dp),
            verticalAlignment = Alignment.Top,
        ) {
            Column(
                modifier = Modifier
                    .weight(if (isBookLayout) 0.5f else 0.46f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                PlayersGrid(
                    game = game,
                    playerName = uiState.playerName,
                    gameState = gameState,
                    uiState = uiState,
                )

                if (gameState.isStarted) {
                    CommentaryTicker(
                        uiState = uiState,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }

            GameContentSection(
                game = game,
                gameState = gameState,
                uiState = uiState,
                isWideLayout = true,
                isPhoneLandscapeLayout = true,
                isTallPane = isBookLayout,
                shouldShowManualDiceGrid = shouldShowManualDiceGrid,
                showPlayerPickerDialog = showPlayerPickerDialog,
                isRolling = isRolling,
                onFinishGame = onFinishGame,
                modifier = Modifier
                    .weight(if (isBookLayout) 0.5f else 0.54f)
                    .fillMaxHeight(),
            )
        }
    }
}

/**
 * A foldable propped up in tabletop mode: the table (dice, pot, whose turn) fills the upper half so
 * everyone around it can watch the roll, and the scorecards sit on the lower half beside the pinned
 * Roll / ¡Banko! buttons, within reach.
 */
@Composable
private fun TabletopGameLayout(
    uiState: BankoGameScreenUiState,
    game: DomainGame?,
    gameState: GameScreenState,
    isRolling: androidx.compose.runtime.MutableState<Boolean>,
    hinge: FoldPosture.Tabletop,
) {
    var boardTopPx by remember { mutableFloatStateOf(0f) }
    val density = LocalDensity.current
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .onGloballyPositioned { boardTopPx = it.positionInWindow().y },
    ) {
        val hingeGap = with(density) { (hinge.hingeBottomPx - hinge.hingeTopPx).toDp() } + 16.dp
        // Everything above the hinge, less a little breathing room over the crease.
        val upperHeight = with(density) { (hinge.hingeTopPx - boardTopPx).toDp() - 8.dp }
            .coerceIn(160.dp, (maxHeight - 120.dp).coerceAtLeast(160.dp))
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            GameContentSection(
                game = game,
                gameState = gameState,
                uiState = uiState,
                isRolling = isRolling,
                modifier = Modifier.height(upperHeight),
            )
            Spacer(modifier = Modifier.height(hingeGap))
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
            ) {
                CommentaryTicker(uiState = uiState, modifier = Modifier.fillMaxWidth())
                PlayersGrid(
                    game = game,
                    playerName = uiState.playerName,
                    gameState = gameState,
                    uiState = uiState,
                )
            }
        }
    }
}

/**
 * A broadcast-style ticker that scrolls snarky play-by-play across the board. Its lane always
 * remains measured so commentary can come and go without moving the game board.
 */
@Composable
private fun CommentaryTicker(
    uiState: BankoGameScreenUiState,
    modifier: Modifier = Modifier,
) {
    val line by uiState.commentaryFlow.collectAsState()
    val tickerText = line.orEmpty()
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(44.dp)
            .padding(horizontal = 12.dp, vertical = 4.dp),
        contentAlignment = Alignment.Center,
    ) {
        AnimatedVisibility(
            visible = tickerText.isNotBlank(),
            enter = fadeIn(tween(durationMillis = 180)),
            exit = fadeOut(tween(durationMillis = 120)),
        ) {
            val density = LocalDensity.current
            val textStyle = MaterialTheme.typography.labelLarge
            val textMeasurer = rememberTextMeasurer()
            val textWidthPx = remember(tickerText, textStyle) {
                textMeasurer.measure(
                    text = AnnotatedString(tickerText),
                    style = textStyle,
                    maxLines = 1,
                    softWrap = false,
                ).size.width
            }
            val textWidthDp = with(density) { textWidthPx.toDp() }
            var tickerLaneWidthPx by remember { mutableStateOf(0) }
            val tickerOffset = remember(tickerText) { Animatable(0f) }
            var tickerStarted by remember(tickerText) { mutableStateOf(false) }

            LaunchedEffect(tickerText, tickerLaneWidthPx, textWidthPx) {
                if (tickerText.isBlank() || tickerLaneWidthPx <= 0 || textWidthPx <= 0) {
                    return@LaunchedEffect
                }

                tickerStarted = false
                tickerOffset.snapTo(tickerLaneWidthPx.toFloat())
                tickerStarted = true
                val travelDistancePx = tickerLaneWidthPx + textWidthPx
                val pxPerSecond = with(density) { 96.dp.toPx() }
                val durationMillis = ((travelDistancePx / pxPerSecond) * 1000)
                    .roundToInt()
                    .coerceIn(4_500, 18_000)

                tickerOffset.animateTo(
                    targetValue = -textWidthPx.toFloat(),
                    animationSpec = tween(durationMillis = durationMillis, easing = LinearEasing),
                )
                uiState.onCommentaryLineFinished(tickerText)
            }

            Surface(
                color = MaterialTheme.colorScheme.inverseSurface,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(36.dp),
                shape = androidx.compose.foundation.shape.RoundedCornerShape(10.dp),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 7.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .size(7.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary),
                    )
                    Spacer(modifier = Modifier.width(10.dp))
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .onSizeChanged { tickerLaneWidthPx = it.width }
                            .clipToBounds(),
                    ) {
                        Text(
                            text = tickerText,
                            color = MaterialTheme.colorScheme.inverseOnSurface,
                            style = textStyle,
                            maxLines = 1,
                            softWrap = false,
                            overflow = TextOverflow.Clip,
                            modifier = Modifier
                                .offset { IntOffset(tickerOffset.value.roundToInt(), 0) }
                                .alpha(if (tickerStarted) 1f else 0f)
                                .requiredWidth(textWidthDp),
                        )
                    }
                }
            }
        }
    }
}
